import React from 'react';
import { CalendarCheck, Check, CheckCircle2, MessageSquareMore, X } from 'lucide-react';
import { Avatar, AvatarFallback } from './Avatar';
import { Badge } from './Badge';
import { Button } from './Button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './Dialog';
import { EmiratiBadge } from './EmiratiBadge';
import { ApplicantTierBadge } from './ApplicantTierBadge';
import { PlatformIcon } from './PlatformIcon';
import { useApplicants } from '../contexts/ApplicantsContext';
import { formatAED, formatCompact, formatDate, getInitials } from '../utils/expert';
import { applicantBreakdown, applicantScore, applicantStatusStyles, getApplicantTier, isOpen } from '../utils/applicant';
type ApplicantReviewDialogProps = {
  applicantId: string | null;
  onClose: () => void;
};
export function ApplicantReviewDialog({
  applicantId,
  onClose
}: ApplicantReviewDialogProps) {
  const {
    applicants,
    setStatus
  } = useApplicants();
  const a = applicants.find(x => x.id === applicantId);
  return <Dialog open={!!a} onOpenChange={o => !o && onClose()} data-id="element-50" data-mp="1pqyy4v898dg9">
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-id="element-51" data-mp="1ut7s9uwv5ljy">
        {a && <>
            <DialogHeader data-id="element-52" data-mp="1zqhi3bo5zz8b">
              <div className="flex items-start gap-3" data-id="element-53" data-mp="1zp6cr7f2mjf3">
                <Avatar size="lg" data-id="element-54" data-mp="24j132tp6lwn5"><AvatarFallback className="font-medium" data-id="element-55" data-mp="24jiy5n03y4th">{getInitials(a.name)}</AvatarFallback></Avatar>
                <div className="min-w-0" data-id="element-56" data-mp="28j1fk8spzxrk">
                  <DialogTitle className="flex flex-wrap items-center gap-2" data-id="element-57" data-mp="2die3kwvbvavq">
                    {a.name}
                    {a.isEmirati && <EmiratiBadge data-id="element-58" data-mp="2ihhbcp2ozv7e" />}
                  </DialogTitle>
                  <DialogDescription data-id="element-59" data-mp="2ngtuayqg0i4p">{a.title} · {a.organization} · {a.location}</DialogDescription>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5" data-id="element-60" data-mp="o3ptj2a7m50v">
                    <ApplicantTierBadge tier={getApplicantTier(a)} data-id="element-61" data-mp="o5stae15zcis" />
                    <Badge variant="outline" className={applicantStatusStyles[a.status]} data-id="element-62" data-mp="t3lxx2mtjygy">{a.status}</Badge>
                    <span className="text-xs text-muted-foreground" data-id="element-63" data-mp="l2mxvyb66ft3">Applied {formatDate(a.appliedOn)}</span>
                  </div>
                </div>
              </div>
            </DialogHeader>

            <div className="grid gap-5 py-4" data-id="element-64" data-mp="v1voqiaqneb8">
              <div className="rounded-lg bg-muted/50 p-3" data-id="element-65" data-mp="q17r3ds07z81">
                <p className="text-xs font-medium text-muted-foreground" data-id="element-66" data-mp="pzwk7539ibcp">Recommended path</p>
                <p className="mt-0.5 text-sm font-medium" data-id="element-67" data-mp="13zefm3b2cmv1">{getApplicantTier(a).reviewPath} · {getApplicantTier(a).entryTier}</p>
              </div>

              <section aria-label="Application score" data-id="element-68" data-mp="1dqitt0sx03ot">
                <div className="flex items-baseline justify-between" data-id="element-69" data-mp="1dq0nfbcjg936">
                  <h3 className="text-sm font-semibold" data-id="element-70" data-mp="18sqon539el2o">Application score</h3>
                  <span className="text-xl font-semibold tabular-nums" data-id="element-71" data-mp="1mqytdz6jeqoe">{applicantScore(a)}<span className="text-xs text-muted-foreground" data-id="element-72" data-mp="1mp46wgfmaqi7"> / 100</span></span>
                </div>
                <ul className="mt-3 grid gap-3 sm:grid-cols-2" data-id="element-73" data-mp="21e5wxjtp0md9">
                  {applicantBreakdown(a).map(f => <li key={f.label} data-id="element-74" data-mp="26dsa5t9iu2m2">
                      <div className="flex justify-between text-xs" data-id="element-75" data-mp="26cqn0qf8dpnz">
                        <span className="font-medium" data-id="element-76" data-mp="1seadystwbn2o">{f.label}</span>
                        <span className="tabular-nums text-muted-foreground" data-id="element-77" data-mp="1xckb38z6kxqx">{f.points.toFixed(1)} / {f.max}</span>
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-muted" data-id="element-78" data-mp="1immrz7g3yy8l">
                        <div className="h-1.5 rounded-full bg-primary" style={{
                    width: `${f.points / f.max * 100}%`
                  }} data-id="element-79" data-mp="1nexvsldyz46f" />
                      </div>
                      <p className="mt-0.5 truncate text-[11px] text-muted-foreground" data-id="element-80" data-mp="19ouca26h5oko">{f.detail}</p>
                    </li>)}
                </ul>
              </section>

              <section aria-label="Motivation" data-id="element-81" data-mp="zpbv1thy8gwb">
                <h3 className="text-sm font-semibold" data-id="element-82" data-mp="14nn4w488urej">Motivation</h3>
                <p className="mt-1 text-sm text-muted-foreground" data-id="element-83" data-mp="14pzjc7esmjmu">“{a.motivation}”</p>
              </section>

              <section aria-label="Highlights" data-id="element-84" data-mp="2735jh3uj9mom">
                <h3 className="text-sm font-semibold" data-id="element-85" data-mp="230sg2m39e0v0">Highlights</h3>
                <ul className="mt-2 space-y-1.5" data-id="element-86" data-mp="1y8r83cd4iwas">
                  {a.highlights.map(h => <li key={h} className="flex gap-2 text-sm" data-id="element-87" data-mp="1ta63z1ur3sh2">
                      <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600" aria-hidden="true" data-id="element-88" data-mp="1t7tr333uo8av" />{h}
                    </li>)}
                </ul>
              </section>

              <div className="grid gap-4 sm:grid-cols-2" data-id="element-89" data-mp="1jduqw9nc6ylk">
                <section aria-label="Fields and rate" className="rounded-lg border p-3" data-id="element-90" data-mp="1fb6i21ymhrol">
                  <p className="text-xs text-muted-foreground" data-id="element-91" data-mp="1abtvzjfkson2">Fields</p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5" data-id="element-92" data-mp="39o60e2ue0zzk">
                    {a.fields.map(f => <Badge key={f} variant="secondary" data-id="element-93" data-mp="39ltn42zkrdsu">{f}</Badge>)}
                  </div>
                  <p className="mt-3 text-xs text-muted-foreground" data-id="element-94" data-mp="34n0byh7rfyvu">Proposed day rate</p>
                  <p className="text-sm font-semibold tabular-nums" data-id="element-95" data-mp="3c8c9eoxuzgmz">{formatAED(a.proposedDailyRate)}</p>
                </section>
                <section aria-label="Social accounts" className="rounded-lg border p-3" data-id="element-96" data-mp="3lyfc5gus88yi">
                  <p className="text-xs text-muted-foreground" data-id="element-97" data-mp="3m0rqzl58u37c">Social accounts</p>
                  <ul className="mt-2 space-y-2" data-id="element-98" data-mp="3qzl2j80yzk4v">
                    {a.accounts.map(acc => <li key={acc.platform} className="flex items-center gap-2 text-sm" data-id="element-99" data-mp="2tbujmd2klqtd">
                        <PlatformIcon platform={acc.platform} className="size-6" data-id="element-100" data-mp="2y931ecjt3su3" />
                        <span className="flex-1 truncate" data-id="element-101" data-mp="3374hcolxrc1c">{acc.handle}</span>
                        <span className="text-xs tabular-nums text-muted-foreground" data-id="element-102" data-mp="379rff7979t6z">{formatCompact(acc.followers)}</span>
                      </li>)}
                  </ul>
                </section>
              </div>
            </div>

            <DialogFooter className="flex-wrap gap-2 sm:justify-between" data-id="element-103" data-mp="ml4sn5hk2fvs">
              {isOpen(a.status) ? <>
                  <div className="flex flex-wrap gap-2" data-id="element-104" data-mp="2elcwsh46kbvj">
                    <Button variant="outline" size="sm" onClick={() => setStatus(a.id, 'Interview')} disabled={a.status === 'Interview'} data-id="element-105" data-mp="2emxps2aujmra">
                      <CalendarCheck data-id="element-106" data-mp="2nl00xaoanvsa" /> Schedule interview
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setStatus(a.id, 'Info requested')} disabled={a.status === 'Info requested'} data-id="element-107" data-mp="1zvnrqasyvnpz">
                      <MessageSquareMore data-id="element-108" data-mp="1vvwtjm1425bf" /> Request info
                    </Button>
                  </div>
                  <div className="flex gap-2" data-id="element-109" data-mp="29n1m64629xc9">
                    <Button variant="destructive" size="sm" onClick={() => setStatus(a.id, 'Rejected')} data-id="element-110" data-mp="24ui8l77dj27g">
                      <X data-id="element-111" data-mp="1h4xlqd83nksw" /> Decline
                    </Button>
                    <Button size="sm" onClick={() => setStatus(a.id, 'Approved')} data-id="element-112" data-mp="1h332d3ch80qx">
                      <Check data-id="element-113" data-mp="1c62xuwv9z61x" /> Approve
                    </Button>
                  </div>
                </> : <div className="flex w-full items-center justify-between gap-2" data-id="element-114" data-mp="3skep52h8ti5u">
                  <p className="text-xs text-muted-foreground" data-id="element-115" data-mp="3smiyw11tpbe1">
                    {a.status === 'Approved' ? 'Approved — onboarding into the expert network.' : 'This application was declined.'}
                  </p>
                  <Button variant="outline" size="sm" onClick={() => setStatus(a.id, 'Under review')} data-id="element-116" data-mp="bcb67si9d5i3">Reopen</Button>
                </div>}
            </DialogFooter>
          </>}
      </DialogContent>
    </Dialog>;
}